import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, limit, getDocs, startAfter, or, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { searchSanctions } from '../services/searchService';
import { Clock, Calendar, Search, AlertCircle, CheckCircle, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const SearchHistory = () => {
  const { currentUser, currentOrg } = useAuth();
  const t = useTranslation();
  const { error, handleError, clearError } = useErrorHandler();
  const navigate = useNavigate();
  
  // State
  const [searchHistory, setSearchHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSearch, setSelectedSearch] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [lastDoc, setLastDoc] = useState(null);
  const [rerunningSearch, setRerunningSearch] = useState(false);
  const [rerunResults, setRerunResults] = useState(null);
  const [showRerunResults, setShowRerunResults] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  
  // Fetch the user's search history
  useEffect(() => {
    if (!currentUser || !currentOrg) return;
    
    fetchSearchHistory();
  }, [currentUser, currentOrg]);
  
  // Effect to perform search when filter text changes
  useEffect(() => {
    if (!currentUser || !currentOrg) return;
    
    if (filterText.trim() === '') {
      // If filter is cleared, fetch regular history
      fetchSearchHistory();
      return;
    }
    
    // Debounce search to avoid too many queries
    const debounceTimer = setTimeout(() => {
      performSearch(filterText);
    }, 500);
    
    return () => clearTimeout(debounceTimer);
  }, [filterText, currentUser, currentOrg]);
  
  // Function to fetch search history
  const fetchSearchHistory = async (loadMore = false) => {
    try {
      setLoading(true);
      
      // Create a reference to the search history collection
      const searchHistoryRef = collection(db, 'sanction-searches');
      
      // Create a query for the current user's searches, ordered by timestamp
      // Add a where clause to filter out mass-upload searches
      let historyQuery;
      
      if (loadMore && lastDoc) {
        historyQuery = query(
          searchHistoryRef,
          where('orgId', '==', currentOrg.id),
          // Only show individual searches, not mass-upload or API searches
          where('source', 'not-in', ['mass-upload', 'api']),
          orderBy('searchTimestampUTC', 'desc'),
          startAfter(lastDoc),
          limit(10)
        );
      } else {
        historyQuery = query(
          searchHistoryRef,
          where('orgId', '==', currentOrg.id),
          // Only show individual searches, not mass-upload or API searches
          where('source', 'not-in', ['mass-upload', 'api']),
          orderBy('searchTimestampUTC', 'desc'),
          limit(10)
        );
      }
      
      // Execute the query
      const querySnapshot = await getDocs(historyQuery);
      
      // Check if there are more results
      setHasMore(querySnapshot.docs.length === 10);
      
      // Save the last document for pagination
      if (querySnapshot.docs.length > 0) {
        setLastDoc(querySnapshot.docs[querySnapshot.docs.length - 1]);
      } else {
        setLastDoc(null);
      }
      
      // Process the results
      const historyItems = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().searchTimestampUTC?.toDate() || new Date()
      }));
      
      // Update state based on whether this is initial load or loading more
      if (loadMore) {
        setSearchHistory(prev => [...prev, ...historyItems]);
      } else {
        setSearchHistory(historyItems);
      }
    } catch (err) {
      handleError(err, { action: 'fetchSearchHistory' });
    } finally {
      setLoading(false);
    }
  };
  
  // Function to search through all records
  const performSearch = async (searchTerm) => {
    if (!searchTerm.trim() || !currentUser || !currentOrg) return;
    
    try {
      setIsSearching(true);
      
      // Create a reference to the search history collection
      const searchHistoryRef = collection(db, 'sanction-searches');
      
      // Create a query for the current organization's searches with the searchTerm
      // Include filter for mass-upload searches
      const historyQuery = query(
        searchHistoryRef,
        where('orgId', '==', currentOrg.id),
        // Only include individual searches, not batch or API searches
        where('source', 'not-in', ['mass-upload', 'api']),
        orderBy('searchTimestampUTC', 'desc'),
        limit(500) // Get more results than we'd show with pagination
      );
      
      // Execute the query
      const querySnapshot = await getDocs(historyQuery);
      
      // Filter results client-side by searchTerm
      const filteredResults = querySnapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data(),
          timestamp: doc.data().searchTimestampUTC?.toDate() || new Date()
        }))
        .filter(item => 
          item.searchTerm && 
          item.searchTerm.toLowerCase().includes(searchTerm.toLowerCase())
        );
      
      // Update state
      setSearchHistory(filteredResults);
      setHasMore(false); // We've loaded all matching results at once
      
    } catch (err) {
      handleError(err, { action: 'performSearch' });
    } finally {
      setIsSearching(false);
    }
  };
  
  // Load more history items - only used when not searching
  const loadMore = () => {
    if (hasMore && !loading && filterText.trim() === '') {
      fetchSearchHistory(true);
    }
  };
  
  // View details of a specific search - redirects to search results page
  const viewSearchDetails = (searchItem) => {
    // Instead of showing details in this component, redirect to main search page with ID
    navigate(`/?id=${searchItem.id}`, { state: { from: 'history' } });
  };
  
  // Re-run a search
  const rerunSearch = async (searchTerm) => {
    try {
      setRerunningSearch(true);
      setRerunResults(null);
      setShowRerunResults(true);
      
      // Execute the search
      const results = await searchSanctions(
        searchTerm, 
        null, // No progress updates needed here
        currentUser.uid
      );
      
      setRerunResults(results);
    } catch (err) {
      handleError(err, { action: 'rerunSearch', searchTerm });
    } finally {
      setRerunningSearch(false);
    }
  };
  
  // Format date for display
  const formatDate = (date) => {
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // Return to the search history list
  const backToList = () => {
    setSelectedSearch(null);
    setShowRerunResults(false);
    setRerunResults(null);
    // Reset filter when going back to list
    setFilterText('');
  };
  
  if (loading && searchHistory.length === 0) {
    return (
      <div className="container mx-auto p-4 max-w-4xl">
        <div className="flex flex-col items-center justify-center p-8 text-center">
          <div className="h-8 w-8 rounded-full border-t-2 border-b-2 border-primary animate-spin"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-300">{t('history.loading')}</p>
        </div>
      </div>
    );
  }
  
  // If viewing a specific search
  if (selectedSearch) {
    return (
      <div className="container mx-auto p-4 max-w-4xl">
        <div className="mb-6">
          <button 
            className="inline-flex items-center text-primary hover:text-primary-dark dark:text-primary-light dark:hover:text-primary transition-colors"
            onClick={backToList}
          >
            <span className="mr-1">←</span> {t('history.backToHistory')}
          </button>
          <h2 className="text-2xl font-semibold mt-4 text-gray-800 dark:text-white">{t('history.searchDetails')}</h2>
        </div>
        
        {/* Search details card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-5 mb-6">
          <div className="mb-4">
            <h3 className="text-xl font-medium text-gray-800 dark:text-white">{selectedSearch.searchTerm}</h3>
            <div className="flex items-center mt-2 text-sm text-gray-500 dark:text-gray-400">
              <Clock size={14} className="mr-1" />
              <span>{formatDate(selectedSearch.timestamp)}</span>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded">
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('history.recordsScanned')}</div>
              <div className="text-xl font-medium text-gray-800 dark:text-white">{selectedSearch.recordsScanned?.toLocaleString() || 0}</div>
            </div>
            <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded">
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('history.matchesFound')}</div>
              <div className="text-xl font-medium text-gray-800 dark:text-white">{selectedSearch.matchesFound?.toLocaleString() || 0}</div>
            </div>
            <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded">
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('history.verificationId')}</div>
              <div className="text-sm font-mono text-gray-800 dark:text-white overflow-x-auto">{selectedSearch.verificationId || 'N/A'}</div>
            </div>
          </div>
          
          {selectedSearch.sourcesChecked && selectedSearch.sourcesChecked.length > 0 && (
            <div className="mt-4">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">{t('history.sourcesChecked')}</div>
              <div className="flex flex-wrap gap-2">
                {selectedSearch.sourcesChecked.map((source, idx) => (
                  <span key={idx} className="inline-block px-2 py-1 text-xs rounded bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                    {source.replace(/-/g, ' ')}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          <div className="mt-5">
            <button 
              className="inline-flex items-center px-4 py-2 bg-primary hover:bg-primary-dark text-white rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => rerunSearch(selectedSearch.searchTerm)}
              disabled={rerunningSearch}
            >
              <Search size={16} className="mr-2" />
              {rerunningSearch ? t('history.runningSearch') : t('history.rerunSearch')}
            </button>
          </div>
        </div>
        
        {/* Matched entities section */}
        {selectedSearch.matchedRecords && selectedSearch.matchedRecords.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-5 mb-6">
            <h3 className="text-lg font-medium text-gray-800 dark:text-white mb-4">{t('history.matchedEntities')}</h3>
            <div className="space-y-2">
              {selectedSearch.matchedRecords.map((recordId, idx) => (
                <div key={idx} className="p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
                  <div className="font-mono text-sm text-gray-800 dark:text-gray-200">{recordId}</div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Re-run results section */}
        {showRerunResults && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-5 mb-6">
            <h3 className="text-lg font-medium text-gray-800 dark:text-white mb-4">{t('history.rerunResults')}</h3>
            
            {rerunningSearch ? (
              <div className="flex flex-col items-center justify-center py-8">
                <div className="h-8 w-8 rounded-full border-t-2 border-b-2 border-primary animate-spin"></div>
                <p className="mt-4 text-gray-600 dark:text-gray-400">{t('history.runningSearch')}</p>
              </div>
            ) : rerunResults ? (
              <div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded">
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('history.recordsScanned')}</div>
                    <div className="text-xl font-medium text-gray-800 dark:text-white">{rerunResults.stats.totalDocumentsQueried?.toLocaleString() || 0}</div>
                  </div>
                  <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded">
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('history.matchesFound')}</div>
                    <div className="text-xl font-medium text-gray-800 dark:text-white">{rerunResults.results.length}</div>
                  </div>
                </div>
                
                {rerunResults.results.length > 0 ? (
                  <div>
                    <h4 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-3">{t('history.matches')}</h4>
                    <div className="space-y-2">
                      {rerunResults.results.slice(0, 5).map((result, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
                          <div className="px-2 py-1 text-xs rounded bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                            {result.confidenceLevel}
                          </div>
                          <div className="flex-1 mx-3 font-medium text-gray-800 dark:text-white">{result.fullName}</div>
                          <div className="text-sm font-medium text-gray-700 dark:text-gray-300">{result.unifiedScore}%</div>
                        </div>
                      ))}
                      {rerunResults.results.length > 5 && (
                        <div className="text-center py-2 text-sm text-gray-600 dark:text-gray-400">
                          + {rerunResults.results.length - 5} {t('history.moreMatches')}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center py-8 text-green-600 dark:text-green-400">
                    <CheckCircle size={24} className="mr-2" />
                    <p className="text-lg">{t('history.noMatches')}</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center py-8 text-red-600 dark:text-red-400">
                <AlertCircle size={24} className="mr-2" />
                <p className="text-lg">{t('history.rerunError')}</p>
              </div>
            )}
          </div>
        )}
        
      </div>
    );
  }
  
  // If showing the search history list
  return (
    <div className="container mx-auto p-4 max-w-4xl">
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-gray-800 dark:text-white">{t('history.searchHistory')}</h2>
        <p className="text-gray-600 dark:text-gray-400 mt-1">{t('history.yourRecentSearches')}</p>
      </div>
      
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-3 rounded-md flex items-center mb-4">
          <AlertCircle size={16} className="mr-2 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
      
      {searchHistory.length === 0 && !isSearching ? (
        <div className="flex flex-col items-center justify-center p-12 text-center text-gray-500 dark:text-gray-400">
          <FileText size={48} className="mb-4 opacity-50" />
          <p className="text-lg font-medium">{t('history.noSearchesYet')}</p>
          <p className="mt-2">{t('history.searchesToAppearHere')}</p>
        </div>
      ) : (
        <>
          <div className="mb-6 relative">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input 
                type="text"
                placeholder={t('history.filterSearches')}
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                className="w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              />
              {filterText && (
                <button 
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                  onClick={() => setFilterText('')}
                  aria-label="Clear filter"
                >
                  <span className="text-xl">×</span>
                </button>
              )}
            </div>
            {isSearching && (
              <div className="flex items-center mt-2 text-sm text-gray-600 dark:text-gray-400">
                <div className="h-3 w-3 rounded-full border-t border-b border-gray-600 dark:border-gray-400 animate-spin mr-2"></div>
                <span>{t('history.searching')}</span>
              </div>
            )}
          </div>
          
          <div className="space-y-3">
            {searchHistory.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-8 text-center text-gray-500 dark:text-gray-400">
                <Search size={24} className="mb-3 opacity-50" />
                <p>{t('history.noMatchingSearches')}</p>
              </div>
            ) : (
              searchHistory.map((item) => (
                <div 
                  key={item.id} 
                  className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700 p-4 hover:shadow-md transition-shadow cursor-pointer flex items-center"
                  onClick={() => viewSearchDetails(item)}
                >
                  <div className="flex-1">
                    <div className="font-medium text-gray-800 dark:text-white">{item.searchTerm}</div>
                    <div className="flex items-center mt-2 text-xs text-gray-500 dark:text-gray-400">
                      <Clock size={14} className="mr-1" />
                      <span>{formatDate(item.timestamp)}</span>
                    </div>
                    <div className="flex flex-wrap gap-x-4 mt-2 text-sm">
                      <div className="text-gray-600 dark:text-gray-300">
                        <span className="text-gray-500 dark:text-gray-400">{t('history.recordsScanned')}:</span>
                        <span className="ml-1 font-medium">
                          {(item.recordsScanned || item.totalRecordsScanned || item.stats?.totalDocumentsQueried || 0).toLocaleString()}
                        </span>
                      </div>
                      <div className="text-gray-600 dark:text-gray-300">
                        <span className="text-gray-500 dark:text-gray-400">{t('history.matchesFound')}:</span>
                        <span className="ml-1 font-medium">
                          {item.matchesFound || (item.matchedRecords?.length) || (item.results?.length) || 0}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="text-primary dark:text-primary-light text-lg">→</div>
                </div>
              ))
            )}
          </div>
          
          {hasMore && filterText === '' && !isSearching && (
            <button 
              className="w-full mt-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-md transition-colors focus:outline-none disabled:opacity-50"
              onClick={loadMore}
              disabled={loading}
            >
              {loading ? t('history.loading') : t('history.loadMore')}
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default SearchHistory;